/** Workflow state machines. Overdue is a calculated flag; "viewed" is activity, not a state. */

export const ENGAGEMENT_STATES = [
  'draft',
  'invited',
  'in_progress',
  'submitted',
  'in_review',
  'changes_requested',
  'awaiting_client_approval',
  'ready_for_handoff',
  'completed',
  'on_hold',
  'cancelled',
] as const;
export type EngagementState = (typeof ENGAGEMENT_STATES)[number];

export const ENGAGEMENT_STATE_LABELS: Record<EngagementState, string> = {
  draft: 'Draft',
  invited: 'Invited',
  in_progress: 'In progress',
  submitted: 'Submitted',
  in_review: 'In review',
  changes_requested: 'Changes requested',
  awaiting_client_approval: 'Awaiting client approval',
  ready_for_handoff: 'Ready for handoff',
  completed: 'Completed',
  on_hold: 'On hold',
  cancelled: 'Cancelled',
};

/**
 * Explicit transitions a staff member may request. Derived transitions (in_progress,
 * submitted, in_review...) are also applied automatically by the engagement deriver.
 */
export const ENGAGEMENT_TRANSITIONS: Record<EngagementState, EngagementState[]> = {
  draft: ['invited', 'cancelled', 'on_hold'],
  invited: ['in_progress', 'on_hold', 'cancelled'],
  in_progress: ['submitted', 'on_hold', 'cancelled'],
  submitted: ['in_review', 'changes_requested', 'on_hold', 'cancelled'],
  in_review: ['changes_requested', 'awaiting_client_approval', 'ready_for_handoff', 'on_hold', 'cancelled'],
  changes_requested: ['in_progress', 'submitted', 'on_hold', 'cancelled'],
  awaiting_client_approval: ['changes_requested', 'ready_for_handoff', 'on_hold', 'cancelled'],
  ready_for_handoff: ['completed', 'in_review', 'on_hold', 'cancelled'],
  completed: [],
  // Resume target is resolved from stored `resumeState`.
  on_hold: ['draft', 'invited', 'in_progress', 'submitted', 'in_review', 'changes_requested', 'awaiting_client_approval', 'ready_for_handoff', 'cancelled'],
  cancelled: [],
};

export const TERMINAL_ENGAGEMENT_STATES: EngagementState[] = ['completed', 'cancelled'];
export const PAUSED_ENGAGEMENT_STATES: EngagementState[] = ['on_hold', 'completed', 'cancelled'];

export function canTransitionEngagement(from: EngagementState, to: EngagementState): boolean {
  return ENGAGEMENT_TRANSITIONS[from]?.includes(to) ?? false;
}

export const FORM_STATES = ['draft', 'in_progress', 'submitted', 'in_review', 'changes_requested', 'accepted'] as const;
export type FormState = (typeof FORM_STATES)[number];
export const FORM_STATE_LABELS: Record<FormState, string> = {
  draft: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
  in_review: 'In review',
  changes_requested: 'Changes requested',
  accepted: 'Accepted',
};
export const FORM_TRANSITIONS: Record<FormState, FormState[]> = {
  draft: ['in_progress', 'submitted'],
  in_progress: ['submitted'],
  submitted: ['in_review', 'changes_requested', 'accepted'],
  in_review: ['changes_requested', 'accepted'],
  changes_requested: ['in_progress', 'submitted'],
  accepted: ['changes_requested'],
};
/** States in which the client may edit the draft. */
export const FORM_EDITABLE_STATES: FormState[] = ['draft', 'in_progress', 'changes_requested'];

export const ASSET_STATES = ['requested', 'provided', 'in_review', 'accepted', 'changes_requested', 'na', 'waived', 'expired'] as const;
export type AssetState = (typeof ASSET_STATES)[number];
export const ASSET_STATE_LABELS: Record<AssetState, string> = {
  requested: 'Requested',
  provided: 'Provided',
  in_review: 'In review',
  accepted: 'Accepted',
  changes_requested: 'Changes requested',
  na: 'Not applicable',
  waived: 'Waived',
  expired: 'Expired',
};

export const ACCESS_STATES = [
  'not_requested',
  'requested',
  'client_granted',
  'verified',
  'rejected',
  'revoked',
  'expired',
  'not_required',
  'waived',
] as const;
export type AccessState = (typeof ACCESS_STATES)[number];
export const ACCESS_STATE_LABELS: Record<AccessState, string> = {
  not_requested: 'Not requested',
  requested: 'Requested',
  client_granted: 'Client marked granted',
  verified: 'Verified',
  rejected: 'Rejected',
  revoked: 'Revoked',
  expired: 'Expired',
  not_required: 'Not required',
  waived: 'Waived',
};
export const ACCESS_TRANSITIONS: Record<AccessState, AccessState[]> = {
  not_requested: ['requested', 'not_required', 'waived'],
  requested: ['client_granted', 'verified', 'not_required', 'waived'],
  client_granted: ['verified', 'rejected', 'requested', 'waived'],
  verified: ['revoked', 'expired'],
  rejected: ['requested', 'client_granted', 'waived'],
  revoked: ['requested'],
  expired: ['requested'],
  not_required: ['requested'],
  waived: ['requested'],
};

export const TASK_STATES = ['open', 'in_progress', 'blocked', 'done', 'cancelled'] as const;
export type TaskState = (typeof TASK_STATES)[number];

export const INVITATION_STATES = ['created', 'sent', 'opened', 'accepted', 'expired', 'revoked'] as const;
export type InvitationState = (typeof INVITATION_STATES)[number];

export const SUBSCRIPTION_STATES = ['trial', 'active', 'past_due', 'grace', 'restricted', 'cancelled'] as const;
export type SubscriptionState = (typeof SUBSCRIPTION_STATES)[number];

export const TEMPLATE_STATES = ['draft', 'published', 'retired'] as const;
export type TemplateState = (typeof TEMPLATE_STATES)[number];

export const NOTIFICATION_STATES = [
  'scheduled',
  'claimed',
  'attempted',
  'provider_accepted',
  'delivered',
  'failed',
  'suppressed',
  'cancelled',
] as const;
export type NotificationState = (typeof NOTIFICATION_STATES)[number];

export const EXPORT_STATES = ['queued', 'running', 'succeeded', 'failed', 'expired'] as const;
export type ExportState = (typeof EXPORT_STATES)[number];

export const SCAN_STATES = ['pending', 'clean', 'infected', 'failed'] as const;
export type ScanState = (typeof SCAN_STATES)[number];
